import { useEffect, useState } from 'react';
import { Table, Select, Button, Space, Tag, Typography, Card, Row, Col, Statistic } from 'antd';
import { CSVLink } from 'react-csv';
import { 
  FileExcelOutlined, 
  EnvironmentOutlined, 
  VideoCameraOutlined, 
  HomeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined
} from '@ant-design/icons';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import moment from "moment";

const { Option } = Select;
const { Text } = Typography;

const TutorClassLogPayment = ({ onChange = () => {} }) => {
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [teacherRates, setTeacherRates] = useState([]);
  const [adminapproveFilter, setAdminapproveFilter] = useState('');
  const [payoutFilter, setPayoutFilter] = useState('');
  const [remarks, setRemarks] = useState({});
  const [approvedClasses, setApprovedClasses] = useState([]);
  const [teacherFilter, setTeacherFilter] = useState('');
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

const today = new Date();

// First day of month (YYYY-MM-DD)
const firstDay = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;

// Last day of month (YYYY-MM-DD)
const lastDateObj = new Date(today.getFullYear(), today.getMonth() + 1, 0);
const lastDay = `${lastDateObj.getFullYear()}-${String(lastDateObj.getMonth() + 1).padStart(2, '0')}-${String(lastDateObj.getDate()).padStart(2, '0')}`;

const [startDate, setStartDate] = useState(firstDay);
const [endDate, setEndDate] = useState(lastDay);


useEffect(() => {
  const fetchClassesAndRates = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [classRes, rateRes, approvedRes, teacherRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/classes/class-by-day`, {
          headers,
          params: { startDate, endDate }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/teacherrate`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/teacher-hours/approved/by-date`, {
          headers,
          params: { startDate, endDate }
        }),
         axios.get(`${process.env.REACT_APP_API_URL}/teachers/select`, { headers })
      ]);

      // Map approved hours and remarks by class_id
      const approvedMap = {};
      const remarksMap = {};
      approvedRes.data.forEach(item => {
        approvedMap[item.class_id] = item.total_amount;
        remarksMap[item.class_id] = item.remark || ''; // Get remarks
      });

      // Merge into classes
      const mergedClasses = classRes.data.map(cls => ({
        ...cls,
        total_amount: approvedMap[cls.classid] ?? null
      }));

      setClasses(mergedClasses);
      setFilteredClasses(mergedClasses);
      setTeacherRates(rateRes.data);
      setApprovedClasses(approvedRes.data);
      setRemarks(remarksMap); // Set remarks state
      setTeacherOptions(teacherRes.data);

    } catch (error) {
      console.error('Error fetching classes, teacher rates, or approved classes:', error);
    }
  };

  fetchClassesAndRates();
}, [startDate, endDate]);



useEffect(() => {
  const filtered = classes.filter(cls => {
    const classDate = new Date(cls.date);
    const classDateStr = classDate.toISOString().split('T')[0];

    const isInRange =
      (!startDate || classDateStr >= startDate) &&
      (!endDate || classDateStr <= endDate);

    return (
      isInRange &&
      (statusFilter ? cls.status === statusFilter : true) &&
      (adminapproveFilter ? cls.adminapprove === adminapproveFilter : true) &&
      (payoutFilter ? cls.payout === payoutFilter : true) &&
      (teacherFilter ? cls.teacher_id === teacherFilter : true) // ⬅️ Added
    );
  });

  setFilteredClasses(filtered);
  setCurrentPage(1);
}, [startDate, endDate, statusFilter, adminapproveFilter, payoutFilter, teacherFilter, classes]);



const calculateEndTime = (startTime, duration) => {
  if (!startTime || !duration) return 'N/A';
  
  // Parse start time
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  
  // Parse duration based on format like "2h 30m"
  let durationHours = 0;
  let durationMinutes = 0;
  
  // Check if duration is in "Xh Ym" format
  if (duration.includes('h') || duration.includes('m')) {
    const hoursMatch = duration.match(/(\d+)h/);
    const minutesMatch = duration.match(/(\d+)m/);
    
    durationHours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
    durationMinutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
  } 
  // If it's in "HH:MM" format
  else if (duration.includes(':')) {
    [durationHours, durationMinutes] = duration.split(':').map(Number);
  }
  
  // Calculate end time
  let endHours = startHours + durationHours;
  let endMinutes = startMinutes + durationMinutes;
  
  if (endMinutes >= 60) {
    endHours += Math.floor(endMinutes / 60);
    endMinutes %= 60;
  }
  
  // Format with leading zeros
  return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
};

const getTeacherRate = (teacherId, platform) => {
  const match = teacherRates.find(r => r.teacher_id === teacherId);
  if (!match) return null;

  switch (platform) {
    case 'onsite':
      return parseFloat(match.onsite_rate);
    case 'online':
      return parseFloat(match.online_rate);
    case 'athome':
      return parseFloat(match.athome_rate);
    default:
      return null;
  }
};

const parseScheduleHourToDecimal = (str) => {
  if (!str) return 0;
  const [hPart, mPart] = str.split(' ');
  const hours = parseInt(hPart.replace('h', '')) || 0;
  const minutes = parseInt(mPart.replace('m', '')) || 0;
  return hours + minutes / 60;
};

// Calculate statistics
const calculateStats = () => {
  const totalHours = filteredClasses.reduce((acc, cls) => {
    return acc + parseScheduleHourToDecimal(cls.schedule_hour);
  }, 0);

  const teacherConfirmedHours = filteredClasses
    .filter(cls => cls.status === 'approved')
    .reduce((acc, cls) => acc + parseScheduleHourToDecimal(cls.schedule_hour), 0);

  const teacherUnconfirmedHours = filteredClasses
    .filter(cls => cls.status !== 'approved')
    .reduce((acc, cls) => acc + parseScheduleHourToDecimal(cls.schedule_hour), 0);

  const adminConfirmedHours = filteredClasses
    .filter(cls => cls.adminapprove === 'adminconfirm')
    .reduce((acc, cls) => acc + parseScheduleHourToDecimal(cls.schedule_hour), 0);

  const adminUnconfirmedHours = filteredClasses
    .filter(cls => cls.adminapprove !== 'adminconfirm')
    .reduce((acc, cls) => acc + parseScheduleHourToDecimal(cls.schedule_hour), 0);

  const paidHours = filteredClasses
    .filter(cls => cls.payout === 'paid')
    .reduce((acc, cls) => acc + parseScheduleHourToDecimal(cls.schedule_hour), 0);

  const unpaidHours = filteredClasses
    .filter(cls => cls.payout === 'pending')
    .reduce((acc, cls) => acc + parseScheduleHourToDecimal(cls.schedule_hour), 0);

  // Calculate total paid amount
  const totalPaidAmount = filteredClasses
    .filter(cls => cls.payout === 'paid' && cls.total_amount !== null && cls.total_amount !== undefined)
    .reduce((acc, cls) => acc + parseFloat(cls.total_amount), 0);

  // Calculate expected unpaid amount (for classes with rate available)
  const expectedUnpaidAmount = filteredClasses
    .filter(cls => cls.payout === 'pending')
    .reduce((acc, cls) => {
      const rate = getTeacherRate(cls.teacher_id, cls.platform);
      const hours = parseScheduleHourToDecimal(cls.schedule_hour);
      if (rate !== null) {
        return acc + (rate * hours);
      }
      return acc;
    }, 0);

  return {
    totalHours: totalHours.toFixed(1),
    teacherConfirmedHours: teacherConfirmedHours.toFixed(1),
    teacherUnconfirmedHours: teacherUnconfirmedHours.toFixed(1),
    adminConfirmedHours: adminConfirmedHours.toFixed(1),
    adminUnconfirmedHours: adminUnconfirmedHours.toFixed(1),
    paidHours: paidHours.toFixed(1),
    unpaidHours: unpaidHours.toFixed(1),
    totalPaidAmount: totalPaidAmount.toFixed(2),
    expectedUnpaidAmount: expectedUnpaidAmount.toFixed(2),
  };
};

const stats = calculateStats();


const csvData = filteredClasses.map(cls => {
  // Calculate time range
  let timeRange = '';
  if (cls.schedule_time && cls.schedule_hour) {
    const startTime = cls.schedule_time.split(':').slice(0, 2).join(':');
    const endTime = calculateEndTime(startTime, cls.schedule_hour);
    timeRange = `${startTime} - ${endTime}`;
  }

  // Platform label
  const platformLabel = {
    onsite: 'Onsite',
    online: 'Online',
    athome: 'At Home',
  }[cls.platform] || cls.platform;

  const rate = getTeacherRate(cls.teacher_id, cls.platform);
  const hours = parseScheduleHourToDecimal(cls.schedule_hour);
  const expectedAmount = rate !== null ? (rate * hours).toFixed(2) : 'N/A';

  return {
    ClassID: cls.classid,
    Date: new Date(cls.date).toLocaleDateString('en-GB'),
    ClassCode: cls.class_code,
    Subject: cls.subject_name,
    Teacher: `(${cls.nickname}) ${cls.teacher_name}`,
    Platform: platformLabel,
    Time: timeRange,
    Hours: cls.schedule_hour,
    Rate: rate ?? 'N/A',
    ExpectedAmount: expectedAmount,
    TotalPaid: cls.total_amount !== undefined && cls.total_amount !== null
      ? `฿ ${Number(cls.total_amount).toLocaleString()}`
      : 'N/A',
    TutorConfirm: cls.status === 'approved' ? 'Approved' : 'Pending',
    AdminConfirm: cls.adminapprove === 'adminconfirm' ? 'Confirmed' : 'Pending',
    Payout: cls.payout === 'pending' ? 'Pending' : 'Paid',
    Remark: remarks[cls.classid] || '-',
  };
});




const csvFilename = startDate && endDate
  ? `tutor_class_log_${startDate}_to_${endDate}.csv`
  : `tutor_class_log_all.csv`;



  const columns = [
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      render: (date) => new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit'
      }),
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
{
  title: t('classCode'),
  dataIndex: 'class_code',
  key: 'class_code',
  width: 250,
  render: (text) => (
    <div
      style={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.3
      }}
    >
      {text}
    </div>
  ),
},

    {
  title: t('classInfo'),
  key: 'classInfo',
  width: 200,
  render: (_, record) => (
    <div style={{ lineHeight: 1.3 }}>
      <div style={{ fontSize: '10px' }}>
        {t('class')}: {record.classid}
      </div>
      <div style={{ color: '#555', whiteSpace: 'normal', wordBreak: 'break-word' }}>
        {record.subject_name || t('noSubject')}
      </div>
    </div>
  )
},
    { 
      title: t('teacher'), 
      dataIndex: 'teacher_name', 
      key: 'teacher_name',
      render: (_, cls) => `${cls.nickname}`
    },
    {
      title: t('platform'),
      dataIndex: 'platform',
      key: 'platform',
      render: (platform) => {
        switch (platform) {
          case 'onsite':
            return (
              <Tag color="blue" icon={<EnvironmentOutlined />}>
                Onsite
              </Tag>
            );
          case 'online':
            return (
              <Tag color="green" icon={<VideoCameraOutlined />}>
                Online
              </Tag>
            );
          case 'athome':
            return (
              <Tag color="orange" icon={<HomeOutlined />}>
                At Home
              </Tag>
            );
          default:
            return <Tag>{platform}</Tag>; // fallback
        }
      },
    },
    { 
      title: t('time'), 
      dataIndex: 'schedule_time', 
      key: 'schedule_time',
      render: (time, record) => {
        if (!time || !record.schedule_hour) return '';
        
        // Remove seconds if present
        const startTime = time.split(':').slice(0, 2).join(':');
        const endTime = calculateEndTime(startTime, record.schedule_hour);
        
        return `${startTime} - ${endTime}`;
      }
    },
    { title: t('hours'), dataIndex: 'schedule_hour', key: 'schedule_hour' },
   {
  title: t('Rate'),
  key: 'rate',
  render: (_, record) => {
    const rate = getTeacherRate(record.teacher_id, record.platform);

    return (
      <Tag
        color="default"
        style={{
          backgroundColor: '#f0f0f0',
          color: '#595959',
          borderRadius: '8px',
          fontWeight: 500,
          fontSize: '14px',
          padding: '4px 10px',
        }}
      >
        {rate !== null ? `฿${rate.toLocaleString()}` : 'N/A'}
      </Tag>
    );
  },
},
{
  title: t('Rate × Hours'),
  key: 'expectedAmount',
  render: (_, record) => {
    const rate = getTeacherRate(record.teacher_id, record.platform);
    const hours = parseScheduleHourToDecimal(record.schedule_hour);

    if (rate !== null) {
      const amount = rate * hours;
      return (
        <Tag
          color="default"
          style={{
            backgroundColor: '#f0f0f0',
            color: '#595959',
            borderRadius: '8px',
            fontWeight: 500,
            fontSize: '14px',
            padding: '4px 10px',
          }}
        >
          ฿{amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Tag>
      );
    }

    return (
      <Tag
        color="default"
        style={{
          backgroundColor: '#f0f0f0',
          color: '#595959',
          borderRadius: '8px',
          fontWeight: 500,
          fontSize: '14px',
          padding: '4px 10px',
        }}
      >
        N/A
      </Tag>
    );
  },
},

    {
  title: t('Actual Paid'),
  key: 'totalPaid',
  render: (_, record) => {
    if (record.total_amount === null || record.total_amount === undefined) {
      return <Tag color="default">N/A</Tag>;
    }

    const amount = Number(record.total_amount);
    const formatted = `฿ ${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

    return (
      <Tag
        color="green"
        style={{
          fontSize: '14px',
          padding: '4px 10px',
          borderRadius: '8px',
          fontWeight: 500,
        }}
      >
        {formatted}
      </Tag>
    );
  },
},
    { 
      title: t('Admin Confirm'), 
      dataIndex: 'adminapprove', 
      key: 'adminapprove',
      render: (adminapprove) => {
        const isConfirmed = adminapprove === 'adminconfirm';
        return (
          <Tag 
            color={isConfirmed ? '#f6ffed' : '#fffbe6'} 
            style={{ 
              color: isConfirmed ? '#52c41a' : '#faad14',
              borderColor: isConfirmed ? '#b7eb8f' : '#ffe58f'
            }}
          >
            {isConfirmed ? t('confirmed') : t('confirmpending')}
          </Tag>
        );
      }
    },
    { 
      title: t('payout'), 
      dataIndex: 'payout', 
      key: 'payout',
      render: (payout) => {
        const isPending = payout === 'pending';
        return (
          <Tag 
            color={isPending ? '#fff1f0' : '#f6ffed'} 
            style={{ 
              color: isPending ? '#f5222d' : '#52c41a',
              borderColor: isPending ? '#ffa39e' : '#b7eb8f'
            }}
          >
            {isPending ? t('Pending') : t('Paid')}
          </Tag>
        );
      }
    },
    {
      title: t('remark'),
      key: 'remark',
      width: 400,
      render: (_, record) => (
        <Text>{remarks[record.classid] || '-'}</Text>
      ),
    }
  ];

  return (
    <div>
      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('Total Hours')}
              value={stats.totalHours}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
              suffix="h"
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {moment(startDate).format('DD MMM YYYY')} - {moment(endDate).format('DD MMM YYYY')}
            </Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('Teacher Confirmed (Taught Classes)')}
              value={stats.teacherConfirmedHours}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
              suffix="h"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('Teacher Unconfirmed')}
              value={stats.teacherUnconfirmedHours}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
              suffix="h"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('Admin Confirmed')}
              value={stats.adminConfirmedHours}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
              suffix="h"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('Admin Unconfirmed')}
              value={stats.adminUnconfirmedHours}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
              suffix="h"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('Paid Hours')}
              value={stats.paidHours}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
              suffix="h"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('Unpaid Hours')}
              value={stats.unpaidHours}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
              suffix="h"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('Actual Paid (฿)')}
              value={stats.totalPaidAmount}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
              precision={2}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title={t('Expected Unpaid (฿)')}
              value={stats.expectedUnpaidAmount}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
              precision={2}
            />
          </Card>
        </Col>
      </Row>

    <div style={{ marginBottom: '20px', background: '#f5f5f5', padding: '15px', borderRadius: '5px' }}>
      
      <Space style={{ marginBottom: 16 }}>
       {/* Start Date */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px' }}>{t('Start Date')}</label>
          <input
            type="date"
            id="hidden-start-date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ display: 'none' }}
          />
          <input
            type="text"
            readOnly
            value={moment(startDate).format('DD-MMM-YY')}
            placeholder="01-Jan-25"
            onClick={() => document.getElementById('hidden-start-date')?.showPicker()}
            style={{
              padding: '4px 8px',
              borderRadius: 4,
              border: '1px solid #d9d9d9',
              cursor: 'pointer',
              width: '120px',
              textAlign: 'center'
            }}
          />
        </div>

        <span>-</span>

        {/* End Date */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px' }}>{t('End Date')}</label>
          <input
            type="date"
            id="hidden-end-date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate}
            style={{ display: 'none' }}
          />
          <input
            type="text"
            readOnly
            value={moment(endDate).format('DD-MMM-YY')}
            placeholder="31-Jan-25"
            onClick={() => document.getElementById('hidden-end-date')?.showPicker()}
            style={{
              padding: '4px 8px',
              borderRadius: 4,
              border: '1px solid #d9d9d9',
              cursor: 'pointer',
              width: '120px',
              textAlign: 'center'
            }}
          />
        </div>

        <div>
        <label style={{ display: 'block', marginBottom: '8px' }}>{t('Filter Teacher')}</label>
        <Select
          value={teacherFilter}
          onChange={setTeacherFilter}
          style={{ width: 200 }}
          placeholder={t('Select Teacher')}
          allowClear
          showSearch
          optionFilterProp="label"
        >
          {teacherOptions.map(teacher => (
            <Option key={teacher.id} value={teacher.id} label={`${teacher.nickname || ''} ${teacher.name}`}>
              {teacher.nickname ? `(${teacher.nickname}) ${teacher.name}` : teacher.name}
            </Option>
          ))}
        </Select>
      </div>

        <div>
        <label style={{ display: 'block', marginBottom: '8px' }}>{t('Filter Status')}</label>
        <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }}>
          <Option value="">{t('classstatuses')}</Option>
          <Option value="approved">{t('confirmed')}</Option>
          <Option value="pending">{t('confirmpending')}</Option>
        </Select>
        </div>
        <div>
        <label style={{ display: 'block', marginBottom: '8px' }}>{t('Filter Approval')}</label>
        <Select value={adminapproveFilter} onChange={setAdminapproveFilter} style={{ width: 160 }}>
          <Option value="">{t('Approve Status')}</Option>
          <Option value="adminconfirm">{t('Admin Confirmed')}</Option> 
          <Option value="pending">{t('Pending')}</Option>
        </Select>
        </div>
        <div>
        <label style={{ display: 'block', marginBottom: '8px' }}>{t('Filter Payment')}</label>
        <Select value={payoutFilter} onChange={setPayoutFilter} style={{ width: 160 }}>
          <Option value="">{t('payout')}</Option>
          <Option value="pending">{t('Pending')}</Option>
          <Option value="paid">{t('Paid')}</Option>
        </Select>
        </div>
        <CSVLink data={csvData} filename={csvFilename}>
          <Button icon={<FileExcelOutlined />} type="primary">
            {t('exportToCSV')}
          </Button>
        </CSVLink>
      </Space>
      </div>

      <div style={{ overflowX: 'auto', width: '100%' }}>
<Table
  columns={columns}
  dataSource={filteredClasses}
  rowKey="classid"
  pagination={{
    current: currentPage,
    pageSize: pageSize,
    showSizeChanger: true,
    pageSizeOptions: ['50', '100', '200'],
    onChange: (page, size) => {
      setCurrentPage(page);
      setPageSize(size);
    }
  }}
  scroll={{ x: '1000px' }}
  size="middle"
  bordered
/>

</div>
    </div>
  );
};

export default withRoleAccess(TutorClassLogPayment, ['superadmin']);